import { SetStateAction } from 'jotai';
import React, { Dispatch, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../auth/UserContext';
import { defaultOptions, ShareInfo } from '../components/shareInfo/ShareInfo';
import { StepsIndexConstant } from '../components/Stepper';
import { useStepper } from '../components/StepperContext';
import type { Entity } from '../constants/customSortFieldConfigs';
import { FORM_WIDGET_COMPONENTS } from '../form/formConstants';
import { FormContainer } from '../form/FormContainer';
import { FormProvider } from '../form/FormContext';
import FormItem from '../form/FormItem';
import FormModal from '../form/FormModal';
import FormWidget from '../form/FormWidget';
import Validator from '../form/Validator';
import I18n from '../i18n/I18n';
import { AppDefinition } from '../nav/apps';
import { useSaveNewReportTemplatePreference } from './reportApiUtils';
import type { TemplatePreference } from './reportTypes';
import { mapPreferencesToTemplates } from './reportUtils';

interface SaveAsTemplateFormModalProps {
    saveAsModalOpen: boolean;
    setSaveAsModalOpen: Dispatch<SetStateAction<boolean>>;
    saveAsName: string | null;
    values: Record<string, any>;
    application: AppDefinition;
    entity: Entity;
    preferenceEntityKey: string;
    setReportTitle: React.Dispatch<React.SetStateAction<string>>;
}

export default function StepCustomizeReport({
    saveAsModalOpen,
    setSaveAsModalOpen,
    saveAsName,
    values,
    application,
    entity,
    preferenceEntityKey,
    setReportTitle
}: SaveAsTemplateFormModalProps) {
    const {
        preferences,
        preferencesLoaded,
        refreshPreferences,
        userProfile,
        activeOrganizationAccount,
        organizationAccountDetails
    } = useContext(UserContext);

    const { getStepData, activeStep, setTemplateData } = useStepper();
    const { saveReportTemplatePreference, saveReportLoading } = useSaveNewReportTemplatePreference({
        userProfile,
        activeOrganizationAccount,
        organizationAccountDetails,
        application,
        entity,
        preferenceEntityKey
    });
    const currentData = getStepData(activeStep);
    const parsedData = typeof currentData?.rich === 'string' ? JSON.parse(currentData?.rich) : currentData?.rich;
    const localTemplates = useMemo(
        () => mapPreferencesToTemplates(preferences, preferenceEntityKey),
        [preferences, preferenceEntityKey]
    );
    const configureStepData = getStepData(StepsIndexConstant.one);
    const [shared, setShared] = useState(false);

    const [saveAsState, setSaveAsState] = useState<Record<string, any>>({
        templateName: saveAsName ?? '',
        templateDescription: '',
        rich: parsedData
    });

    const handleSave = async () => {
        const newTemplate = (await saveReportTemplatePreference(
            saveAsState,
            values.rich,
            setSaveAsModalOpen,
            configureStepData,
            shared
        )) as TemplatePreference;

        if (newTemplate) {
            setReportTitle(saveAsState.templateName.trim());
            setTimeout(refreshPreferences, 2000);
        }
    };

    useEffect(() => {
        if (preferencesLoaded && localTemplates && !saveAsModalOpen) {
            setTemplateData(StepsIndexConstant.zero, [...localTemplates]);
        }
    }, [preferencesLoaded, localTemplates]);

    useEffect(() => {
        setSaveAsState((prev) => ({
            ...prev,
            templateName: saveAsName ?? ''
        }));
    }, [saveAsName]);

    return (
        <FormModal
            id={'save-template-as-modal'}
            isOpen={saveAsModalOpen}
            onClose={() => setSaveAsModalOpen(false)}
            title={<I18n token="report.template.save.as.modal.title" />}
            onSave={handleSave}
            isSaving={saveReportLoading}
            isSaveDisabled={!saveAsState.templateName?.trim()}
        >
            <FormProvider
                validationRules={{ templateName: [Validator.RULES.isRequired] }}
                state={saveAsState}
                setState={setSaveAsState}
                allowMultipleExpansion
            >
                <FormContainer containerSx={{ p: 2 }}>
                    <FormItem>
                        <FormWidget name="templateName" label={<I18n token="form.resource.name" />} />
                    </FormItem>

                    <FormItem>
                        <FormWidget
                            name="templateDescription"
                            component={FORM_WIDGET_COMPONENTS.textArea}
                            label={<I18n token="report.template.save.as.description" />}
                        />
                    </FormItem>

                    <FormItem>
                        <ShareInfo options={defaultOptions} shared={shared} onSharedChange={setShared} />
                    </FormItem>
                </FormContainer>
            </FormProvider>
        </FormModal>
    );
}
